import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  CContainer,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft } from "@coreui/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import UserTable from "./components/UserTable";
import UserModal from "./components/UserModal";
import StatsModal from "./components/StatsModal";
import "./UserManagementPage.css";

const UserManagementPage = () => {
  const { t } = useTranslation();
  const isStaff = localStorage.getItem("is_staff") === "true";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });

  const [modal, setModal] = useState({
    type: null,
    data: null,
    visible: false,
  });
  const [statsState, setStatsState] = useState({ visible: false, user: null });

  const fetchUsers = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`/api/users/?page=${page}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(res.data.results);
        setPagination({
          currentPage: page,
          totalPages: Math.ceil(res.data.count / 20),
        });
      } catch (err) {
        toast.error(t("common.error"));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    fetchUsers(1);
  }, [fetchUsers]);

  const handleSaveUser = async () => {
    const { type, data } = modal;
    const token = localStorage.getItem("token");

    if (type === "password" && data.password.length < 8) {
      toast.warn("Password too short.");
      return;
    }

    try {
      if (type === "edit") {
        await axios.patch(`/api/users/${data.id}/`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(t("users.update_success"));
      } else if (type === "password") {
        await axios.post(
          `/api/users/${data.id}/set-password/`,
          { new_password: data.password },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success(t("users.password_success"));
      }
      setModal({ type: null, data: null, visible: false });
      fetchUsers(pagination.currentPage);
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const openModal = (type, data = null) => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    const initialData = {
      ...data,
      ...(type === "password" && { password: "" }),
    };
    setModal({ type, data: initialData, visible: true });
  };

  return (
    <div className="bg-light min-vh-100 p-4">
      <CContainer fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>{t("users.title")}</h2>
          <CButton color="secondary" as={Link} to="/dashboard">
            <CIcon icon={cilArrowLeft} className="me-2" /> {t("common.back")}
          </CButton>
        </div>

        <CCard>
          <CCardHeader>{t("users.list_title")}</CCardHeader>
          <CCardBody>
            <UserTable
              users={users}
              loading={loading}
              pagination={pagination}
              fetchUsers={fetchUsers}
              openModal={openModal}
              openStatsModal={(user) => setStatsState({ visible: true, user })}
            />
          </CCardBody>
        </CCard>
      </CContainer>

      <UserModal
        modal={modal}
        setModal={setModal}
        handleSave={handleSaveUser}
        closeModal={() => setModal({ type: null, data: null, visible: false })}
      />

      <StatsModal
        visible={statsState.visible}
        user={statsState.user}
        onClose={() => setStatsState({ visible: false, user: null })}
      />
    </div>
  );
};

export default UserManagementPage;